import i18n from 'i18n'

const METRIC_LOCALE = 'ar-EG-u-nu-latn'

const CURRENCY_CODE_TO_KEY: Record<string, string> = {
  EGP: 'common.currency.EGP',
  SAR: 'common.currency.SAR',
  AED: 'common.currency.AED',
  KWD: 'common.currency.KWD',
  QAR: 'common.currency.QAR',
  BHD: 'common.currency.BHD',
  OMR: 'common.currency.OMR',
  JOD: 'common.currency.JOD',
  LBP: 'common.currency.LBP',
}

const STATIC_CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
}

export function defaultCurrencySymbol(): string {
  return i18n.t('common.currency.EGP')
}

/** @deprecated Use defaultCurrencySymbol() */
export const DEFAULT_CURRENCY_SYMBOL = defaultCurrencySymbol

function currencySymbolForCode(code: string): string {
  const key = CURRENCY_CODE_TO_KEY[code]
  if (key) return i18n.t(key)
  return STATIC_CURRENCY_SYMBOLS[code] ?? defaultCurrencySymbol()
}

/** Prefer explicit symbol from settings; map ISO codes (e.g. EGP) to Arabic labels. */
export function resolveCurrencySymbol(
  symbol?: string | null,
  code?: string | null,
): string {
  const sym = String(symbol ?? '').trim()
  const cod = String(code ?? '').trim().toUpperCase()

  if (sym && !/^[A-Z]{3}$/i.test(sym)) {
    return sym
  }

  const iso = cod || sym.toUpperCase()
  if (iso) {
    return currencySymbolForCode(iso)
  }

  return defaultCurrencySymbol()
}

/** Formatted amount with currency suffix, e.g. "4,836.00 ج.م". */
export function formatMoney(
  value: number | string | null | undefined,
  currencySymbol?: string,
): string {
  return formatCurrencyMetricParts(value, currencySymbol ?? defaultCurrencySymbol()).full
}

export type MetricKpiTier = 'primary' | 'secondary'

export type CurrencyMetricParts = {
 amount: string
 currency: string
 full: string
 isNegative: boolean
}

/** Integer / count metrics with thousands separators. */
export function formatMetricNumber(value: number | string | null | undefined): string {
 const num = Number(value)
 if (!Number.isFinite(num)) return '0'
 return num.toLocaleString(METRIC_LOCALE, { maximumFractionDigits: 0 })
}

/** Currency metric: formatted amount + separate currency label (presentation only). */
/** Parse numeric strings that may include thousands separators. */
export function parseMetricAmount(value: number | string | null | undefined): number {
 if (typeof value === 'number') {
 return Number.isFinite(value) ? value : 0
 }
 const raw = String(value ?? '').trim().replace(/,/g, '')
 const num = Number(raw)
 return Number.isFinite(num) ? num : 0
}

export function formatCurrencyMetricParts(
 value: number | string | null | undefined,
 currencySymbol = defaultCurrencySymbol(),
): CurrencyMetricParts {
 const safe = parseMetricAmount(value)
 const amount = safe.toLocaleString(METRIC_LOCALE, {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
 })
 const currency = resolveCurrencySymbol(currencySymbol)
 return {
 amount,
 currency,
 full: `${amount} ${currency}`,
 isNegative: safe < 0,
 }
}

/** Responsive typography based on visible display length (digits + separators). */
export function getMetricValueSizeClass(
 displayText: string,
 tier: MetricKpiTier = 'primary',
): string {
 const len = displayText.replace(/\s/g, '').length
 const primary = tier === 'primary'

 if (len <= 6) {
 return primary ? 'text-2xl sm:text-3xl': 'text-xl sm:text-2xl'
 }
 if (len <= 10) {
 return primary ? 'text-xl sm:text-2xl': 'text-lg sm:text-xl'
 }
 if (len <= 14) {
 return primary ? 'text-lg sm:text-xl': 'text-base sm:text-lg'
 }
 return primary ? 'text-base sm:text-lg': 'text-sm sm:text-base'
}

/** Parse pre-formatted dashboard strings like"1,250,000.00 ج.م". */
export function parseMetricValueString(value: string): {
 isCurrency: boolean
 amount: string
 currency?: string
 full: string
 isNegative: boolean
} {
 const trimmed = String(value ?? '').trim()
 if (!trimmed) {
 return { isCurrency: false, amount: '0', full: '0', isNegative: false }
 }

 const currencyMatch = trimmed.match(/^(-?[\d,]+(?:\.\d+)?)\s+(\S.+)$/)
 if (currencyMatch) {
 const amount = currencyMatch[1]
 return {
 isCurrency: true,
 amount,
 currency: currencyMatch[2],
 full: trimmed,
 isNegative: amount.startsWith('-'),
 }
 }

 return {
 isCurrency: false,
 amount: trimmed,
 full: trimmed,
 isNegative: trimmed.startsWith('-'),
 }
}
